import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../components/AuthContext";
import Loading from "../components/Loading";
import LoadingWithProgress from "../components/LoadingWithProgress";
import Swal from "sweetalert2";
import { FaEye, FaEyeSlash, FaChevronDown } from "react-icons/fa";
import { validateEmail, validatePassword } from "../utils/validation";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [rememberDuration, setRememberDuration] = useState('7d'); // Default 7 days
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { login, loading, currentDomain, loginLoading, loginProgress, loginMessage } = useContext(AuthContext);
  const navigate = useNavigate();

  // Duration options for custom select
  const durationOptions = [
    { value: '1d', label: '1 Day' },
    { value: '3d', label: '3 Days' },
    { value: '7d', label: '1 Week' },
    { value: '14d', label: '2 Weeks' },
    { value: '30d', label: '1 Month' },
    { value: '60d', label: '2 Months' },
    { value: '90d', label: '3 Months' }
  ];

  // Get selected option label
  const getSelectedLabel = () => {
    const selected = durationOptions.find(option => option.value === rememberDuration);
    return selected ? selected.label : 'Choose duration...';
  };

  // Handle option selection
  const handleOptionSelect = (value) => {
    setRememberDuration(value);
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.custom-select-container')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  
  const validateEmailField = (email) => {
    const validation = validateEmail(email);
    return validation.isValid;
  };

  // Enhanced password validation using utility
  const validatePasswordField = (password) => {
    const validation = validatePassword(password, 6);
    return validation.isValid;
  };

  // Real-time validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    if (value) {
      const validation = validateEmail(value);
      setEmailError(validation.isValid ? "" : validation.error);
    } else {
      setEmailError("");
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    
    if (value) {
      const validation = validatePassword(value, 6);
      setPasswordError(validation.isValid ? "" : validation.error);
    } else {
      setPasswordError("");
    }
  };

  // Check if form is valid
  const isFormValid = validateEmailField(email) && validatePasswordField(password) && !emailError && !passwordError;

  // Check for terms acceptance cookie on mount
  useEffect(() => {
    const cookies = document.cookie.split(";").map((cookie) => cookie.trim());
    const termsAcceptedCookie = cookies.find((cookie) =>
      cookie.startsWith("termsAccepted=")
    );
    if (!termsAcceptedCookie) {
      Swal.fire({
        title: "Terms and Conditions",
        html: 'By using this site, you agree to our <a href="/terms-of-use" target="_blank" class="text-indigo-600 underline">Terms of Use</a> and <a href="/privacy-policy" target="_blank" class="text-indigo-600 underline">Privacy Policy</a>.',
        showCancelButton: false,
        confirmButtonText: "Accept",
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          const date = new Date();
          date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year
          document.cookie = `termsAccepted=1; expires=${date.toUTCString()}; path=/`;
        }
      });
    }
  }, []);

  if (loading) {
    return <Loading />;
  }
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setLoginError("");
    setEmailError("");
    setPasswordError("");
      // Validate form before submission using utility
    if (!validateEmailField(email)) {
      const validation = validateEmail(email);
      setEmailError(validation.error);
      return;
    }
    
    if (!validatePasswordField(password)) {
      const validation = validatePassword(password, 6);
      setPasswordError(validation.error);
      return;
    }
    
    try {
      const result = await login(email, password, rememberMe ? rememberDuration : null);
      
      if (!result) {
        setLoginError("Login failed. Please try again.");
        return;
      }

      if (result.error) {
        // Check if it's an email verification error
        if (result.needsVerification) {
          const loginInfo = result.loginCount ? 
            `You have completed ${result.loginCount} logins. ` : '';
          const limitInfo = result.loginLimit ? 
            `Verification is now required after ${result.loginLimit} logins.` : 
            'Please verify your email address to continue.';
            
          Swal.fire({
            icon: 'warning',
            title: 'Email Verification Required',
            html: `${loginInfo}${limitInfo}<br><br>Would you like to verify your email now?`,
            showCancelButton: true,
            confirmButtonText: 'Verify Now',
            cancelButtonText: 'Cancel'
          }).then((response) => {
            if (response.isConfirmed) {
              navigate('/verify-email', { state: { email: result.email || email } });
            }
          });
          return;
        }
        
        setLoginError(result.error);
        return;
      }

      // Check if user is not verified (for successful login)
      if (result.isVerified === false) {
        Swal.fire({
          icon: 'info',
          title: 'Account Not Verified',
          html: 'Your account is not verified yet. Some features may be limited.<br><br>Would you like to verify your email now?',
          showCancelButton: true,
          confirmButtonText: 'Verify Now',
          cancelButtonText: 'Continue Without Verification',
          allowOutsideClick: false
        }).then((response) => {
          if (response.isConfirmed) {
            navigate('/verify-email', { state: { email: result.userEmail || email } });
            return;
          }
          // If user chooses to continue, proceed with normal navigation
          navigateToRoleDashboard(result);
        });
        return;
      }

      // Proceed with normal navigation
      navigateToRoleDashboard(result);
    } catch (error) {
      console.error("Login error:", error);
      setLoginError("Login failed. Please try again.");
    }
  };

  // Helper function to navigate based on role
  const navigateToRoleDashboard = (result) => {
    // Handle different roles
    switch (result.role) {
      case "SuperAdmin":
        navigate("/super-admin/dashboard", { replace: true });
        break;
      
      case "Admin":
        if (!result.domainMatch) {
          // Show warning in a more subtle way
          console.warn(`No explicit domain access found for '${currentDomain}'. Some features may be limited.`);
          // Still proceed with login
        }
        navigate("/admin/dashboard", { replace: true });
        break;
      
      case "CompanyHead":
        if (!result.domainMatch) {
          console.warn(`You don't have explicit access to domain '${currentDomain}'. Domain-specific features may be restricted.`);
          // Still proceed with login
        }
        navigate("/company-head/dashboard", { replace: true });
        break;
      
      case "Employee":
        if (!result.domainMatch) {
          console.warn(`You don't have explicit access to domain '${currentDomain}'. Some features may be restricted.`);
          // Still proceed with login
        }
        navigate("/employee/dashboard", { replace: true });
        break;
      
      case "Support":
        // Support agents operate the support tracker, not the MIS.
        window.location.href = "https://support.sanibiz.in";
        break;
      
      default:
        setLoginError("Unknown user role");
        return;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-700 to-pink-600 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse delay-1000"></div>
        <div className="absolute w-64 h-64 bg-pink-500/20 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse delay-500"></div>
      </div>

      {/* Two Card Layout Container */}
      <div className="w-full max-w-6xl mx-auto relative z-20">
        <div className="flex items-center justify-center gap-8 lg:gap-12">
          
          {/* Card 1: Logo Card with Glassmorphism */}
          <div className="hidden lg:block animate-fadeSlideIn">
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-12 shadow-2xl border border-white/20 hover:bg-white/15 transition-all duration-500 hover:scale-105 hover:rotate-1">
              <div className="text-center">
                <div className="inline-block bg-white/20 backdrop-blur-md rounded-3xl p-6 shadow-xl mb-6">
                  <img 
                    src="/sanibiz.png"  
                    alt="SaniBiz Logo" 
                    className="w-40 h-40 rounded-2xl shadow-2xl"
                  />
                </div>
                <h2 className="text-4xl font-bold text-white drop-shadow-lg mb-3 tracking-tight">SaniBiz</h2>
                <p className="text-white/90 text-lg font-medium">Management Information System</p>
                <p className="text-white/80 text-base mt-2 italic font-light">Where Automation Meets Accuracy</p>
                
                <div className="mt-6 pt-4 border-t border-white/20">
                  <p className="text-white/70 text-xs font-medium text-center">Designed by Star Solutions</p>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Main Login Card */}
          <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-10 w-full max-w-md border border-white/30 hover:shadow-3xl transition-all duration-300 animate-fadeSlideIn delay-200">
            {/* Mobile Logo */}
            <div className="lg:hidden flex justify-center mb-8">
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-10 shadow-2xl hover:scale-105 transition-transform duration-300">
                <img 
                  src="/sanibiz.png"  
                  alt="SaniBiz Logo" 
                  className="w-40 h-40 rounded-2xl"
                />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                Welcome Back
              </h1>
              <p className="text-gray-600 font-medium">Sign in to continue to SaniBiz MIS</p>
            </div>

            {/* Continuous Loading Progress */}
            {loginLoading && (
              <div className="mb-6 animate-fadeSlideIn">
                <div className="relative">
                  {/* Progress bar container with glassmorphism */}
                  <div className="bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 rounded-full h-3 overflow-hidden shadow-inner border border-indigo-200/50">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                      style={{ width: `${loginProgress}%` }}
                    >
                      {/* Animated shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
                    </div>
                  </div>
                  
                  {/* Loading message */}
                  <div className="mt-3 text-center">
                    <p className="text-sm font-semibold text-indigo-600 flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {loginMessage || 'Signing you in...'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{loginProgress}% complete</p>
                  </div>
                </div>
              </div>
            )}

            {/* Login Error Display */}
            {loginError && (
              <div className="mb-4 p-4 bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 text-red-700 rounded-xl text-sm animate-fadeSlideIn shadow-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold">{loginError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-6">
              {/* Email Field */}
              <div className="group">
                <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide flex items-center gap-2">
                  <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    className={`w-full px-5 py-3.5 border-2 rounded-xl focus:outline-none transition-all duration-300 placeholder-gray-400 text-sm font-medium ${
                      emailError 
                        ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-4 focus:ring-red-100 shake' 
                        : 'border-gray-200 bg-white/90 backdrop-blur-sm focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 hover:border-indigo-300 hover:shadow-lg group-hover:shadow-xl'
                    }`}
                    placeholder="your.email@example.com"
                    disabled={loginLoading}
                  />
                  {email && !emailError && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                {emailError && (
                  <p className="text-red-600 text-xs mt-2 flex items-center gap-1.5 animate-fadeSlideIn font-semibold">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {emailError}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="group">
                <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide flex items-center gap-2">
                  <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    className={`w-full px-5 py-3.5 pr-12 border-2 rounded-xl focus:outline-none transition-all duration-300 placeholder-gray-400 text-sm font-medium ${
                      passwordError 
                        ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-4 focus:ring-red-100 shake' 
                        : 'border-gray-200 bg-white/90 backdrop-blur-sm focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 hover:border-indigo-300 hover:shadow-lg group-hover:shadow-xl'
                    }`}
                    placeholder="Enter your password"
                    disabled={loginLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-indigo-600 transition-all duration-200 focus:outline-none p-2 rounded-lg hover:bg-indigo-50 active:scale-95"
                    disabled={loginLoading}
                  >
                    {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                </div>
                {passwordError && (
                  <p className="text-red-600 text-xs mt-2 flex items-center gap-1.5 animate-fadeSlideIn font-semibold">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {passwordError}
                  </p>
                )}
              </div>

              {/* Remember Me Section */}
              <div className="mb-2">
                <div className="flex items-center bg-gradient-to-r from-indigo-50 to-purple-50 p-3 rounded-xl border border-indigo-100">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 focus:ring-2 border-gray-300 rounded transition-all duration-200 cursor-pointer"
                    disabled={loginLoading}
                  />
                  <label htmlFor="remember-me" className="ml-3 block text-sm text-gray-800 font-semibold cursor-pointer">
                    Remember me for longer
                  </label>
                </div>
                
                {rememberMe && (
                  <div className="ml-1 mt-4 animate-fadeSlideIn">
                    <div className="w-full relative custom-select-container">
                      {/* Custom Select Trigger */}
                      <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        disabled={loginLoading}
                        className={`w-full px-4 py-3.5 text-left border-2 rounded-xl focus:outline-none transition-all duration-300 text-sm font-semibold bg-white/90 backdrop-blur-sm shadow-md hover:shadow-xl ${
                          isDropdownOpen 
                            ? 'border-indigo-500 ring-4 ring-indigo-100 scale-[1.02]' 
                            : 'border-gray-200 hover:border-indigo-300'
                        } ${loginLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            <span className={`${rememberDuration ? 'text-gray-800' : 'text-gray-400'}`}>
                              {getSelectedLabel()}
                            </span>
                          </div>
                          <FaChevronDown 
                            className={`w-4 h-4 text-indigo-500 transition-transform duration-300 ${
                              isDropdownOpen ? 'rotate-180' : 'rotate-0'
                            }`} 
                          />
                        </div>
                      </button>

                      {/* Custom Dropdown Options */}
                      {isDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-xl border-2 border-indigo-200 rounded-xl shadow-2xl z-50 overflow-hidden animate-fadeSlideIn">
                          <div className="py-2 max-h-64 overflow-y-auto">
                            {durationOptions.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => handleOptionSelect(option.value)}
                                disabled={loginLoading}
                                className={`w-full px-5 py-3.5 text-left text-sm font-semibold transition-all duration-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-700 focus:outline-none focus:bg-indigo-50 focus:text-indigo-700 ${
                                  rememberDuration === option.value 
                                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600' 
                                    : 'text-gray-700'
                                } ${loginLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span>{option.label}</span>
                                  {rememberDuration === option.value && (
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3 px-2">
                      <svg className="w-4 h-4 text-indigo-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <p className="text-xs text-gray-600 font-semibold">
                        Stay logged in for the selected duration
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Login Button - Show only when form is valid and not loading */}
              {isFormValid && !loginLoading && (
                <button
                  type="submit"
                  className="w-full py-4 rounded-xl text-base font-bold text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 focus:outline-none focus:ring-4 focus:ring-purple-300 shadow-2xl hover:shadow-3xl transition-all duration-300 ease-in-out animate-fadeSlideIn transform hover:scale-[1.02] active:scale-[0.98] tracking-wide relative overflow-hidden group"
                  disabled={loginLoading}
                >
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    <svg className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    Sign In to Continue
                  </span>
                  {/* Animated gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                </button>
              )}
              
              {/* Show loading button when login is in progress */}
              {loginLoading && (
                <button
                  type="button"
                  className="w-full py-4 rounded-xl text-base font-bold text-white bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 cursor-not-allowed shadow-2xl animate-fadeSlideIn tracking-wide relative overflow-hidden"
                  disabled
                >
                  <div className="flex items-center justify-center gap-3">
                    <div className="relative">
                      <div className="animate-spin rounded-full h-5 w-5 border-3 border-white border-t-transparent"></div>
                      <div className="absolute inset-0 animate-ping rounded-full h-5 w-5 border-2 border-white opacity-20"></div>
                    </div>
                    <span>Signing you in...</span>
                  </div>
                  {/* Continuous shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                </button>
              )}
            </form>            {/* Forgot Password and Verify Email Links */}
            <div className="mt-8 text-center">
              <div className="flex items-center justify-center space-x-2 flex-wrap gap-2">
                <button
                  onClick={() => navigate('/password-reset')}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-bold transition-all duration-200 hover:underline decoration-2 underline-offset-4 flex items-center gap-1.5 px-3 py-1 rounded-lg hover:bg-indigo-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Forgot Password?
                </button>
                <span className="text-gray-300 font-bold">•</span>
                <button
                  onClick={() => navigate('/verify-email')}
                  className="text-sm text-green-600 hover:text-green-800 font-bold transition-all duration-200 hover:underline decoration-2 underline-offset-4 flex items-center gap-1.5 px-3 py-1 rounded-lg hover:bg-green-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Verify Email
                </button>
                <span className="text-gray-300 font-bold">•</span>
                <a
                  href="https://support.sanibiz.in"
                  className="text-sm text-blue-600 hover:text-blue-800 font-bold transition-all duration-200 hover:underline decoration-2 underline-offset-4 flex items-center gap-1.5 px-3 py-1 rounded-lg hover:bg-blue-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 18v-6a9 9 0 0118 0v6M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z" />
                  </svg>
                  Support Team
                </a>
              </div>
            </div>

            {/* Version Details */}
            <div className="mt-6 text-center">
              <div className="inline-block bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-2 rounded-full border border-gray-200">
                <p className="text-xs text-gray-600 font-semibold">
                  Version {process.env.REACT_APP_VERSION} • {process.env.REACT_APP_LAST_UPDATE}
                </p>
              </div>
            </div>
            
            {/* Terms of Use and Privacy Policy links */}
            <div className="mt-6 text-center border-t border-gray-100 pt-6">
              <p className="text-xs text-gray-600 leading-relaxed font-medium">
                By signing in, you agree to our
                <Link to="/terms-of-use" className="text-indigo-600 hover:text-indigo-800 font-bold ml-1.5 hover:underline decoration-2 underline-offset-2">
                  Terms of Use
                </Link>
                <span className="mx-1.5">and</span>
                <Link
                  to="/privacy-policy"
                  className="text-indigo-600 hover:text-indigo-800 font-bold hover:underline decoration-2 underline-offset-2"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>
          {/* End of Card 2: Login Card */}
          
        </div>
        {/* End of flex container */}
      </div>
      {/* End of Two Card Layout Container */}

      {/* Add custom animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-5px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(5px);
          }
        }
        
        .animate-fadeSlideIn {
          animation: fadeSlideIn 0.5s ease-out;
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .shake {
          animation: shake 0.5s;
        }
        
        .delay-500 {
          animation-delay: 500ms;
        }
        
        .delay-1000 {
          animation-delay: 1000ms;
        }
        
        .delay-200 {
          animation-delay: 200ms;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
